import { Dispatch, SetStateAction } from 'react';

import {
  ChakraHStack,
  FAIconName,
  FireflyButton,
  FireflyTertiaryButtonMenuItem,
  ModalV2,
  MultiSelectOption,
  Text,
} from '@equip.health/ui';

import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useLazyGetNoteTemplateAutopopulateDataQuery } from '~/store/services/notes/notes';

interface NoteTemplateSwitchModalProps {
  clickedTemplateId: string | null;
  noteTemplateRevisions: NoteTemplateRevision[];
  patientId: string;
  setClickedTemplateId: Dispatch<SetStateAction<string | null>>;
  setFieldValue: (
    key: string, // TODO this can probably be typed even better, to a list of specific keys of the state type
    value:
      | boolean
      | string
      | Date
      | MultiSelectOption
      | Array<MultiSelectOption>
      | FireflyTertiaryButtonMenuItem
      // @TODO Resolves eslint error caused by calling setFieldValue using data returned by getNoteTemplateAutopopulateData. Should this be allowed?
      | Record<string, object>,
  ) => void;
  isOpen: boolean;
  isRevisionDataLoading: boolean;
  closeModal: () => void;
  onCancel: () => void;
}

const modalArgs = {
  bodyStyle: {
    overflow: 'visible',
    padding: '312px',
  },
  footerStyle: {
    padding: '0px',
  },
  titleStyle: {
    flex: '1 0 0',
    padding: '0px',
  },
};

const TOP_LEVEL_ID = 'note-template-switch';

const NoteTemplateSwitchModal = ({
  clickedTemplateId,
  noteTemplateRevisions,
  patientId,
  setClickedTemplateId,
  setFieldValue,
  isOpen,
  isRevisionDataLoading,
  closeModal,
  onCancel,
}: NoteTemplateSwitchModalProps) => {
  const {
    currentUser: { userId: currentUserId },
  } = useGlobalContext();

  const [
    getNoteTemplateAutopopulateData,
    { isLoading: isGetNoteTemplateAutopopulateDataLoading },
  ] = useLazyGetNoteTemplateAutopopulateDataQuery();

  const onConfirm = () => {
    const selectedTemplateRevision = noteTemplateRevisions.find(
      (ntr) => ntr.templateRevisionId === clickedTemplateId,
    );
    if (selectedTemplateRevision) {
      setFieldValue('noteTemplateRevision', {
        label: selectedTemplateRevision.template?.templateTitle,
        node: selectedTemplateRevision.templateRevisionId,
      });

      getNoteTemplateAutopopulateData({
        patientId,
        providerId: currentUserId,
        templateRevisionId: selectedTemplateRevision.templateRevisionId,
      }).then((result) => {
        setFieldValue('noteTemplateRevisionData', result?.data || {});
        closeModal();
      });
    } else {
      setFieldValue('noteTemplateRevision', NO_NOTE_TEMPLATE_OPTION);
      setFieldValue('noteTemplateRevisionData', null);
      setClickedTemplateId(null);
      closeModal();
    }
  };

  const isAnApiLoading =
    isGetNoteTemplateAutopopulateDataLoading || isRevisionDataLoading;

  return (
    <ModalV2
      footerContent={
        <ChakraHStack
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          paddingTop="6px"
          width="100%"
        >
          <FireflyButton
            id={`${TOP_LEVEL_ID}-modal__cancel`}
            isLoading={isAnApiLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={onCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-modal__confirm`}
            isLoading={isAnApiLoading}
            leftIcon={FAIconName.repeatRegular}
            onClick={onConfirm}
          >
            Switch template
          </FireflyButton>
        </ChakraHStack>
      }
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onCancel}
      onEscape={onCancel}
      size="xl"
      title="Switching note templates"
      {...modalArgs}
    >
      <Text alignSelf="stretch" id={`${TOP_LEVEL_ID}-modal__body`}>
        Switching to a different template will discard all existing note
        content, including saved data.
      </Text>
      <Text mt="10px">Are you sure you want to proceed?</Text>
    </ModalV2>
  );
};

export default NoteTemplateSwitchModal;
